/**
 * Persistence for the inline Bible context disclosures (M.3.8.f.1.c +
 * f.2). Two independent localStorage-backed Sets track per-block
 * "user collapsed this against its default" decisions.
 *
 * Persistence policy (locked):
 *   - Default-expanded block (book context on chapter 1, chapter context
 *     on chapter 2+): user collapse persists; user expand-back clears.
 *   - Default-collapsed block (book context on chapter 2+, chapter
 *     context on chapter 1): per-visit only; toggling does NOT touch
 *     persistence.
 *
 * Net effect: the persistence Sets only grow when a user dismisses a
 * default-shown block, and only shrink when they re-engage. A user
 * cannot pin a default-collapsed block as expanded across visits in
 * f.1.c/f.2; reversible via a future "reset preferences" surface.
 *
 * Storage shape mirrors leafGlowSeenIds in src/lib/settings-store.ts:
 *   - Module-scoped Set<string> with lazy initialization on first read.
 *   - try/catch around all localStorage access (private-mode Safari).
 *   - JSON-stringified Array.from(Set) on write; JSON.parse + Array
 *     validation on read.
 *
 * bookSlug here is the route-param kebab-case slug ('genesis',
 * '1-samuel'), NOT the canonical book name. The composite key for the
 * chapter Set is `${bookSlug}:${chapter}` (e.g. 'genesis:1').
 */

const BOOK_KEY = 'adia-bible-book-context-collapsed';
const CHAPTER_KEY = 'adia-bible-chapter-context-collapsed';

let bookCache: Set<string> | null = null;
let chapterCache: Set<string> | null = null;

function readSet(key: string): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    const result = new Set<string>();
    for (const v of parsed) {
      if (typeof v === 'string' && v) result.add(v);
    }
    return result;
  } catch {
    return new Set();
  }
}

function writeSet(key: string, set: Set<string>): void {
  try {
    localStorage.setItem(key, JSON.stringify(Array.from(set)));
  } catch {
    // Private-mode / disabled-storage: keep in-memory only.
  }
}

function getBookCache(): Set<string> {
  if (bookCache === null) bookCache = readSet(BOOK_KEY);
  return bookCache;
}

function getChapterCache(): Set<string> {
  if (chapterCache === null) chapterCache = readSet(CHAPTER_KEY);
  return chapterCache;
}

function chapterCompositeKey(bookSlug: string, chapter: number): string {
  return `${bookSlug}:${chapter}`;
}

export function isBookContextCollapsed(bookSlug: string): boolean {
  return getBookCache().has(bookSlug);
}

export function setBookContextCollapsed(
  bookSlug: string,
  collapsed: boolean,
): void {
  const cache = getBookCache();
  if (collapsed) {
    if (cache.has(bookSlug)) return;
    cache.add(bookSlug);
  } else {
    if (!cache.has(bookSlug)) return;
    cache.delete(bookSlug);
  }
  writeSet(BOOK_KEY, cache);
}

export function isChapterContextCollapsed(
  bookSlug: string,
  chapter: number,
): boolean {
  return getChapterCache().has(chapterCompositeKey(bookSlug, chapter));
}

export function setChapterContextCollapsed(
  bookSlug: string,
  chapter: number,
  collapsed: boolean,
): void {
  const cache = getChapterCache();
  const key = chapterCompositeKey(bookSlug, chapter);
  if (collapsed) {
    if (cache.has(key)) return;
    cache.add(key);
  } else {
    if (!cache.has(key)) return;
    cache.delete(key);
  }
  writeSet(CHAPTER_KEY, cache);
}

export function defaultExpanded(
  chapter: number,
  kind: 'book' | 'chapter',
): boolean {
  // 2026-06-13: both contexts now default COLLAPSED (the inverse of the
  // former f.1.d "context is part of reading" rule). A white chevron with a
  // gold glow cues that each block is tap-to-expand. Because this returns
  // false uniformly, BibleContextBlock.handleToggle's
  // `if (defaultExpandedValue) onPersistedChange(...)` branch never fires:
  // expansion is PER-VISIT (re-collapses on chapter navigation) per rule
  // #70's locked "default-collapsed -> per-visit only" semantics, and the
  // persistence Sets / set*Collapsed helpers + any existing
  // adia-bible-*-context-collapsed localStorage entries are now inert
  // (harmlessly ignored by the `false && !persistedCollapsed` initializer).
  //
  // Signature retained for possible future per-kind or per-chapter rules.
  // The chapter and kind args are intentionally unused; void statements
  // mark the intent. Do not delete the args without planner ratification.
  void chapter;
  void kind;
  return false;
}
